import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import ProductCard from './components/ProductCard';
import CategoryFilter from './components/CategoryFilter';
import OrderForm from './components/OrderForm';
import { products } from './data/products';

function App() {
  const [selectedCategory, setSelectedCategory] = useState('Все');
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartAnimation, setCartAnimation] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 48;
  const sessionStart = useRef(Date.now()); // Сохранение времени начала сессии

  const categories = ['Все', 'Аппараты', 'Дрожжи', 'Бутылки', 'Вкусовые добавки', 'Наборы трав', 'Очистка', 'Емкости для брожения', 'Казаны и печи', 'Автоклавы', 'Комплектующие к С.А.', 'Пивные смеси', 'Акссесуары'];

  // Отправка данных о посещении при загрузке и разгрузке страницы
  useEffect(() => {
    const logVisit = async () => {
      const sessionTime = (Date.now() - sessionStart.current) / 1000; // Время в секундах
      try {
        await fetch('/visit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_time: sessionTime }),
        });
      } catch (error) {
        console.error('Ошибка логирования посещения:', error);
      }
    };

    logVisit(); // Логируем при загрузке

    // Логируем при закрытии вкладки или разлоге
    const handleUnload = () => logVisit();
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      logVisit(); // Логируем при размонтировании
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // Оптимизированная фильтрация с мемоизацией
  const filteredProducts = useCallback(() => {
    const filtered = selectedCategory === 'Все'
      ? products
      : products.filter(product => product.category === selectedCategory && product.category);
    return filtered;
  }, [selectedCategory]);

  // Логика добавления в корзину
  const addToCart = useCallback((product) => {
    const itemCount = cart.filter(item => item.id === product.id).length;
    if (itemCount >= 75) {
      alert('Нельзя добавить больше 75 единиц одного товара!');
      return;
    }
    setCartAnimation(true);
    setTimeout(() => setCartAnimation(false), 300);
    setCart(prevCart => [...prevCart, { ...product, instanceId: Date.now() + Math.random() }]);
  }, [cart]);

  // Обновление количества в корзине
  const updateQuantity = useCallback((id, newQuantity) => {
    const num = newQuantity === '' ? 0 : Math.max(0, Math.min(75, parseInt(newQuantity) || 0));
    if (num === 0) {
      setCart(prevCart => prevCart.filter(item => item.id !== id));
      return;
    }
    const currentItems = cart.filter(item => item.id === id);
    const diff = num - currentItems.length;
    const newCart = [...cart];

    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        const product = products.find(p => p.id === id);
        if (product) newCart.push({ ...product, instanceId: Date.now() + i });
      }
    } else if (diff < 0) {
      for (let i = 0; i < -diff; i++) {
        const index = newCart.findIndex(item => item.id === id && newCart.length > num);
        if (index !== -1) newCart.splice(index, 1);
      }
    }
    setCart(newCart);
  }, [cart]);

  // Удаление всех единиц товара
  const removeFromCart = useCallback((id) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  }, []);

  // Обработчик смены категории с дебаунсом
  const handleCategoryChange = useCallback((category) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    const timeout = setTimeout(() => {
      setSelectedCategory(category);
      setCurrentPage(1);
    }, 300);
    setDebounceTimeout(timeout);
  }, [debounceTimeout]);

  const toggleCart = () => setIsCartOpen(!isCartOpen);

  // Пагинация
  const paginate = useCallback(() => {
    const productsToShow = filteredProducts();
    const totalPages = Math.ceil(productsToShow.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return productsToShow.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const totalPages = useCallback(() => Math.ceil(filteredProducts().length / itemsPerPage), [filteredProducts, itemsPerPage]);

  const paginateHandler = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const form = document.querySelector('.order-form');
    if (isCartOpen && form) form.classList.add('active');
    else if (form) form.classList.remove('active');
  }, [isCartOpen]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className={`App light ${isCartOpen ? 'cart-open' : ''}`}>
      <header>
        <img src="/images/logo.png" alt="Алхимики" className="logo" />
        <div className="header-controls">
          <div className="contact-info">
            <a href="tel:+79080079879" className="contact-item">
              <img src="/icons/phone.svg" alt="Phone" className="custom-icon" />
              +7 908 007-98-79
            </a>
            <a href="https://t.me/Alhimiki_support" target="_blank" rel="noopener noreferrer" className="contact-item">
              <img src="/icons/telegram.svg" alt="Telegram" className="custom-icon" />
              Telegram
            </a>
          </div>
          <div className="separator"></div>
          <button className={`cart-icon ${cartAnimation ? 'animate-cart' : ''}`} onClick={toggleCart}>
            <img src="/icons/cart.svg" alt="Cart" className="icon" />
            {cart.length > 0 && <span className="cart-count">{cart.length}</span>}
          </button>
        </div>
      </header>
      <main>
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={handleCategoryChange}
        />
        <div className="product-grid">
          {paginate().map((product, index) => (
            <ProductCard
              key={`${product.id}-${selectedCategory}-${currentPage}-${index}`}
              product={{ ...product, image: product.image || '/images/not-found.jpg' }}
              addToCart={addToCart}
            />
          ))}
        </div>
        {totalPages() > 1 && (
          <div className="pagination">
            {Array.from({ length: totalPages() }, (_, i) => i + 1).map(number => (
              <button
                key={number}
                onClick={() => paginateHandler(number)}
                className={currentPage === number ? 'active' : ''}
              >
                {number}
              </button>
            ))}
          </div>
        )}
        {isCartOpen && (
          <OrderForm
            cart={cart}
            setCart={setCart}
            removeFromCart={removeFromCart}
            updateQuantity={updateQuantity}
            closeCart={() => setIsCartOpen(false)}
            isVisible={isCartOpen}
          />
        )}
      </main>
      <footer>
        <div className="footer-content">
          <h3>Контакты</h3>
          <p><strong>Телефон:</strong> <a href="tel:+79080079879">+7 908 007-98-79</a></p>
          <p><strong>Telegram:</strong> <a href="https://t.me/Alhimiki_support" target="_blank" rel="noopener noreferrer">https://t.me/Alhimiki_support</a></p>
          <p><strong>Адрес:</strong> <a href="https://2gis.ru/kurgan/geo/70000001029185644" target="_blank" rel="noopener noreferrer">Ул. К. Маркса, 93, Курган</a></p>
          <p><strong>Режим работы:</strong> Пн-Пт 10:00-19:00, Сб-Вс 10:00-18:00</p>
          <p>Магазин оборудования и ингредиентов для варения самогона.</p>
        </div>
      </footer>
      {showScrollTop && (
        <button className="scroll-top-btn" onClick={scrollToTop}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default App;
